import { Group } from "../../group";
import { CheckboxGroup } from "../../checkbox";
import { Children, forwardRef } from "react";
import { arrayify, augmentElement, isNil, isNumber, mergeProps, useAutoFocusChild, useEventCallback, useFocusManager, useFocusScope, useMergedRefs } from "../../shared";
import { RadioGroup } from "../../radio";
import { useResponsiveValue } from "../../styling";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "div";
const UnselectableGroup = /*#__PURE__*/forwardRef(({
  as = DefaultElement,
  autoFocus,
  children,
  ...rest
}, ref) => {
  const [focusScope, setFocusRef] = useFocusScope();
  const groupRef = useMergedRefs(setFocusRef, ref);
  const focusManager = useFocusManager(focusScope);
  useAutoFocusChild(focusManager, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus
  });
  return /*#__PURE__*/_jsx(Group, { ...mergeProps(rest, {
      as,
      ref: groupRef
    }),
    children: children
  });
});
const GroupType = {
  "multiple": CheckboxGroup,
  "none": UnselectableGroup,
  "single": RadioGroup
};

function denormalizeValue(value, selectionMode) {
  if (!isNil(value) && selectionMode === "single") {
    return value[0];
  }

  return value;
}

export function InnerTileGroup({
  children,
  defaultValue,
  disabled,
  forwardedRef,
  onChange,
  rowSize = 1,
  selectionMode = "none",
  value,
  ...rest
}) {
  const rowSizeValue = useResponsiveValue(rowSize);
  const As = GroupType[selectionMode];
  const handleChange = useEventCallback((event, newValue) => {
    if (!isNil(onChange)) {
      onChange(event, arrayify(newValue));
    }
  });
  return /*#__PURE__*/_jsx(As, { ...mergeProps(rest, {
      fluid: true,
      // If you change the gap, also update the tile size gap (currently 16px) below.
      gap: 4,
      orientation: "horizontal",
      ref: forwardedRef,
      wrap: true
    }, selectionMode === "none" ? {} : {
      defaultValue: denormalizeValue(defaultValue, selectionMode),
      disabled,
      onChange: handleChange,
      value: denormalizeValue(value, selectionMode)
    }),
    children: Children.toArray(children).filter(x => x).map(x => {
      return augmentElement(x, {
        disabled: selectionMode === "none" ? disabled : undefined,
        orientation: "horizontal",
        width: `calc((100% - ${(rowSizeValue - 1) * 16}px) / ${rowSizeValue})`
      });
    })
  });
}
InnerTileGroup.defaultElement = DefaultElement;
export const TileGroup = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerTileGroup, { ...props,
  forwardedRef: ref
}));